import React, { useRef, useState } from "react";
import PropTypes from "prop-types";
import { Offcanvas, Spinner, Overlay, Tooltip } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import moment from "moment";
import SubtleBadge from "components/common/subtleBadge";

const DASH = "—";

const Field = ({ label, children }) => (
  <div>
    <p className="mb-0 text-muted fw-medium font-size-13">{label}</p>
    <div className="text-muted d-flex align-items-center gap-1 mb-0 fw-normal font-size-12">
      {children}
    </div>
  </div>
);

Field.propTypes = {
  label: PropTypes.string.isRequired,
  children: PropTypes.node,
};

const StatusBadge = ({ statusMeta, t, isSmall = false, isLive = false }) => {
  if (!statusMeta) return DASH;

  const badgeColor = isLive ? "primary" : statusMeta.badgeColor;
  const isDashed = !isLive && statusMeta.dashed;
  const label = isLive ? t("attendanceOverview.inProgress", "In progress") : t(statusMeta.labelKey, statusMeta.label);

  return (
    <SubtleBadge pill bg={badgeColor} className={`${isDashed ? "badge-dashed" : ""} ${isSmall ? "px-2 py-1" : ""}`.trim()}>
      {isSmall ? (
        <span className="fs-11 fw-medium">{label}</span>
      ) : (
        <p className="mb-0 data-font-size">{label}</p>
      )}
    </SubtleBadge>
  );
};

  StatusBadge.propTypes = {
    statusMeta: PropTypes.object,
    t: PropTypes.func.isRequired,
    isSmall: PropTypes.bool,
    isLive: PropTypes.bool,
  };

  const PlaceLabel = ({ place }) => {
    const [isTooltipVisible, setIsTooltipVisible] = useState(false);
    const [tooltipId] = useState(() => `place-${Math.random().toString(36).slice(2)}`);
    const labelRef = useRef(null);
    const pointerTypeRef = useRef("mouse");

    const isMousePointer = () => pointerTypeRef.current === "mouse";
    const toggleTooltip = () => setIsTooltipVisible((prev) => !prev);

    const handleOutsideTap = (event) => {
      if (labelRef.current?.contains(event?.target)) return;
      setIsTooltipVisible(false);
    };

    return (
      <>
        <span
          ref={labelRef}
          className="place-label"
          tabIndex={0}
          onPointerDown={(event) => {
            pointerTypeRef.current = event.pointerType;
          }}
          onPointerEnter={(event) => {
            if (event.pointerType === "mouse") setIsTooltipVisible(true);
          }}
          onPointerLeave={(event) => {
            if (event.pointerType === "mouse") setIsTooltipVisible(false);
          }}
          onClick={() => {
            if (!isMousePointer()) toggleTooltip();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") {
              event.preventDefault();
              toggleTooltip();
            }
          }}
        >
          {place.city || place.address}
        </span>

        <Overlay target={labelRef} show={isTooltipVisible} placement="bottom" rootClose onHide={handleOutsideTap}>
          {(overlayProps) => (
            <Tooltip id={tooltipId} {...overlayProps}>
              {place.address || place.city}
            </Tooltip>
          )}
        </Overlay>
      </>
    );
  };

  PlaceLabel.propTypes = {
    place: PropTypes.shape({ city: PropTypes.string, address: PropTypes.string }).isRequired,
  };

  const AttendanceSidePanel = ({
    show,
    onHide,
    employee = null,
    date = null,
    history = [],
    isHistoryLoading = false,
    hasHistoryError = false,
  }) => {
  const { t } = useTranslation();

  if (!employee) return null;

  const { dayData, statusMeta } = employee;
  const subtitle = employee.role ? t(`roles.${employee.role}`, employee.role) : t("employee", "Employee");
  const renderLocation = (place) =>
    place?.city || place?.address ? <PlaceLabel place={place} /> : DASH;

  const selectedPlace = history.find((entry) => entry.date === moment(date).format("YYYY-MM-DD"))?.place;
  const sortedHistory = [...history].sort((a, b) => b.date.localeCompare(a.date));

  return (
    <Offcanvas show={show} onHide={onHide} placement="end" className="attendance-offcanvas">
      <Offcanvas.Header closeButton className="border-bottom py-3 px-3">
        <div className="d-flex align-items-center">
          <div
            className={`avatar avatar-xl rounded-circle ${employee.avatarClass} d-flex align-items-center justify-content-center me-3`}
          >
            <span className="fw-bold fs-9">{employee.initials}</span>
          </div>
          <div>
            <h4 className="mb-1 text-truncate">{employee.name}</h4>
            <div className="fs-10 text-500">{subtitle}</div>
          </div>
        </div>
      </Offcanvas.Header>

      <Offcanvas.Body className="p-3 data-font-size">
        <div className="row g-3 pb-3 border-bottom mb-3">
          <div className="col-4">
            <Field label={t("date", "Date")}>{moment(date).format("DD-MM-YYYY")}</Field>
          </div>
          <div className="col-4">
            <Field label={t("location", "Location")}>{renderLocation(selectedPlace)}</Field>
          </div>
          <div className="col-4">
            <Field label={t("status", "Status")}>
              <StatusBadge statusMeta={statusMeta} t={t} isSmall isLive={dayData?.isLive} />
            </Field>
          </div>
          <div className="col-4">
            <Field label={t("clockIn", "Clock-in")}>{dayData?.checkIn || DASH}</Field>
          </div>
          <div className="col-4">
            <Field label={t("clockOut", "Clock-out")}>
              {dayData?.checkOut || (dayData?.isLive ? t("attendanceOverview.live", "Live") : DASH)}
            </Field>
          </div>
        </div>

          <h3 className="fw-medium font-size-15 mb-2">{t("attendanceOverview.last7Days")}</h3>

        {isHistoryLoading ? (
          <div className="text-center py-4">
            <Spinner animation="border" size="sm" />
          </div>
        ) : (
          <div className="table-responsive scrollbar">
            <table className="table table-sm mb-0 fs-11 align-middle attendance-panel-table">
              <thead className="bg-200 text-700 text-nowrap fs-11">
                <tr>
                  <th>{t("date")}</th>
                  <th>{t("status")}</th>
                  <th>{t("location")}</th>
                  <th>{t("workedHours")}</th>
                </tr>
              </thead>
              <tbody>
                {sortedHistory.map((entry) => {
                  const m = moment(entry.date);
                  const d = entry.dayData;
                  const isSelected = entry.date === moment(date).format("YYYY-MM-DD");
                  return (
                    <tr key={entry.date}>
                      <td className="text-nowrap fs-11">{m.format("DD-MM")}</td>
                      <td className="text-nowrap fs-11">{d.isFuture ? DASH : <StatusBadge statusMeta={entry.statusMeta} t={t} isSmall isLive={d.isLive} />}</td>
                      <td className="fs-11">{renderLocation(entry.place)}</td>
                      <td className="text-700 text-nowrap fs-11">
                        {d.isLive ? t("attendanceOverview.live") : d.formattedHours || DASH}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Offcanvas.Body>
    </Offcanvas>
  );
};

AttendanceSidePanel.propTypes = {
  show: PropTypes.bool.isRequired,
  onHide: PropTypes.func.isRequired,
  employee: PropTypes.object,
  date: PropTypes.string,
  history: PropTypes.array,
  isHistoryLoading: PropTypes.bool,
  hasHistoryError: PropTypes.bool,
};

export default AttendanceSidePanel;